import { useCallback, useEffect, useState } from 'react';
import type { MediaItem, Profile } from '../shared/types';
import { Gallery } from './components/Gallery';
import { Lightbox } from './components/Lightbox';
import { Login } from './components/Login';
import { UploadTray } from './components/UploadTray';
import { ApiRequestError, deleteMedia, deleteSession, getMedia } from './lib/api';

type AppStatus = 'loading' | 'login' | 'gallery';

export default function App() {
  const [status, setStatus] = useState<AppStatus>('loading');
  const [profile, setProfile] = useState<Profile | null>(
    () => localStorage.getItem('bigbear_profile') as Profile | null,
  );
  const [items, setItems] = useState<MediaItem[]>([]);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    const loaded: MediaItem[] = [];
    let cursor: string | undefined;

    do {
      const page = await getMedia(cursor);
      loaded.push(...page.items);
      setItems([...loaded]);
      cursor = page.nextCursor;
    } while (cursor);

    return loaded;
  }, []);

  useEffect(() => {
    load()
      .then(() => setStatus('gallery'))
      .catch((error) => {
        if (error instanceof ApiRequestError && error.status === 401) {
          setStatus('login');
        } else {
          setNotice(error instanceof Error ? error.message : 'Could not load the gallery');
          setStatus('gallery');
        }
      });
  }, [load]);

  const authenticated = (nextProfile: Profile) => {
    localStorage.setItem('bigbear_profile', nextProfile);
    setProfile(nextProfile);
    setStatus('loading');
    load()
      .then(() => setStatus('gallery'))
      .catch((error) => {
        setNotice(error instanceof Error ? error.message : 'Could not load the gallery');
        setStatus('gallery');
      });
  };

  const signOut = async () => {
    await deleteSession();
    localStorage.removeItem('bigbear_profile');
    setItems([]);
    setProfile(null);
    setStatus('login');
  };

  const remove = async (item: MediaItem) => {
    if (!window.confirm(`Move “${item.name}” and its display copy to Drive Trash?`)) return;

    const previous = items;
    setItems((current) => current.filter((candidate) => candidate.id !== item.id));
    setSelected(null);

    try {
      await deleteMedia(item.id);
      setNotice('Moved to Drive Trash');
    } catch (error) {
      setItems(previous);
      setSelected(item);
      setNotice(error instanceof Error ? error.message : 'Delete failed. The item was restored.');
    }
  };

  const moveLightbox = useCallback(
    (direction: -1 | 1) => {
      if (!selected) return;

      const index = items.findIndex((item) => item.id === selected.id);
      const next = (index + direction + items.length) % items.length;
      setSelected(items[next]);
    },
    [items, selected],
  );

  if (status === 'loading') {
    return (
      <main className="loading-screen">
        <div className="loading-screen__mark" aria-hidden="true" />
        <p>Opening Big Bear…</p>
      </main>
    );
  }

  if (status === 'login') return <Login onAuthenticated={authenticated} />;

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="site-header__title">
          <p className="eyebrow">Siderman family gallery · 2026</p>
          <h1>Big Bear</h1>
        </div>
        <div className="site-header__session">
          <span>{profile ?? 'Family'}</span>
          <button type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      <main>
        <section className="gallery-intro">
          <p>July 21—24</p>
          <h2>Four days by the lake.</h2>
          <span>{items.length} memories</span>
        </section>

        <Gallery items={items} onSelect={setSelected} />
      </main>

      <UploadTray
        onComplete={(item) => {
          setItems((current) => [item, ...current.filter((candidate) => candidate.id !== item.id)]);
        }}
      />

      {selected && (
        <Lightbox
          item={selected}
          position={items.findIndex((item) => item.id === selected.id)}
          count={items.length}
          onClose={() => setSelected(null)}
          onMove={moveLightbox}
          onDelete={remove}
        />
      )}

      {notice && (
        <button className="toast" type="button" onClick={() => setNotice('')} aria-label={`${notice}. Dismiss`}>
          {notice}
        </button>
      )}
    </div>
  );
}
